import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import ConfirmationModal from "../../components/ConfirmationModal/ConfirmationModal";
import NavigationLink from "../../components/NavigationLink/NavigationLink";
import DriveBindings from "../DriveBindings/DriveBindings";
import { EditActions, editActions } from "../../actions/editActions";
import { PdApiActions, pdApiActions } from "../../actions/pdApiActions";
import { ColumnActions, columnActions } from "../../actions/columnActions";
import {
  editedObjectIdSelector,
  isEditActiveSelector,
  editedObjectIdentifierSelector,
  domainSelector
} from "../../reducers/editReducer";

import DriveParametersTable from "./Parameters/DriveParametersTable";
import DriveFaultsTable from "./Faults/DriveFaultsTable";
import { ParameterCategoriesTable } from "../common/ParameterCategories/ParameterCategoriesTable";
import DriveSignalsTable from "./Signals/DriveSignalsTable";
import DriveSignalGroupsTable from "./SignalGroups/DriveSignalGroupsTable";
import DriveTestsTable from "./Tests/DriveTestsTable";
import ActionMenu from "../common/ActionMenu/ActionMenu";
import {
  setFaultExtraProperties,
  setParameterExtraProperties,
  createEmptyParameter,
  createEmptyFault,
  createEmptyDriveSignal,
  createEmptyDriveTest,
  createEmptyDriveSignalGroup,
  createEmptyParameterCategory,
  setParameterCategoryEnumExtraProperties,
  setDriveSignalExtraProperties
} from "../../utils/pdApiUtils";
import ContentLayout from "../../components/ContentLayout/ContentLayout";
import { mainActions } from "../../actions/mainActions";
import { Filter } from "../../components/Table/Migration/LegacyTypes";

type StateProps = {
  pdApi: State.PdApi;
  columns: State.ColumnMap;
  mainState: State.Main;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  objectIdentifier: string;
  domainName: string;
  isLoadingParameters: boolean;
  isLoadingFaults: boolean;
  filterOptions: State.FilterOptions;
};

type DispatchProps = {
  pdApiActions: typeof pdApiActions;
  columnActions: typeof columnActions;
  editActions: typeof editActions;
  mainActions: typeof mainActions;
};

type OwnProps = {
  subMenuRoute: State.DriveSubmenu;
  userFiltering?: Filter[];
};

// Props of the component
export type DriveProps = StateProps & DispatchProps & OwnProps;

type DriveState = {
  isModalOpen: boolean;
  tableColumnFilters: {
    [key in State.DriveSubmenu]: Filter[];
  };
};

export class Drive extends React.Component<DriveProps, DriveState> {
  state = {
    isModalOpen: false,
    tableColumnFilters: {
      "Drive Faults": [],
      "Drive Parameters": [],
      "Drive Bindings": [],
      "Parameter Categories": [],
      "Drive Signals": [],
      "Drive Tests": [],
      "Drive Signal Groups": []
    }
  };
  private groupName: Api.GroupName = "Drive";
  /**
   * To add new sub page to this parent, add it here
   */
  subPages: Internal.SubPages = {
    "Drive Parameters": {
      itemName: "parameter",
      PdApiField: "driveParameters",
      dataView: DriveParametersTable,
      createEmpty: createEmptyParameter,
      setExtraProperties: setParameterExtraProperties
    },

    "Drive Faults": {
      itemName: "fault",
      PdApiField: "driveFaults",
      dataView: DriveFaultsTable,
      createEmpty: createEmptyFault,
      setExtraProperties: setFaultExtraProperties
    },
    "Drive Bindings": {
      itemName: "binding",
      createEmpty: () => {
        /*  */
      },
      PdApiField: "domains",
      dataView: DriveBindings,
      setExtraProperties: null
    },
    "Drive Signals": {
      itemName: "signal",
      PdApiField: "signals",
      dataView: DriveSignalsTable,
      createEmpty: createEmptyDriveSignal,
      setExtraProperties: setDriveSignalExtraProperties
    },
    "Drive Tests": {
      itemName: "test",
      PdApiField: "driveTests",
      dataView: DriveTestsTable,
      createEmpty: createEmptyDriveTest,
      setExtraProperties: null
    },
    "Drive Signal Groups": {
      itemName: "signal group",
      PdApiField: "signalGroups",
      dataView: DriveSignalGroupsTable,
      createEmpty: createEmptyDriveSignalGroup,
      setExtraProperties: null
    },
    "Parameter Categories": {
      itemName: "parameter category",
      PdApiField: "parameterCategories",
      dataView: ParameterCategoriesTable,
      createEmpty: createEmptyParameterCategory,
      setExtraProperties: setParameterCategoryEnumExtraProperties
    }
  };

  componentDidMount(): void {
    if (
      this.props.pdApi.driveFaults === undefined &&
      window.location.href.includes("drive/faults")
    ) {
      this.props.pdApiActions.fetchDriveFaults();
    }
    if (
      this.props.pdApi.driveParameters === undefined &&
      window.location.href.includes("drive/parameters")
    ) {
      this.props.pdApiActions.fetchDriveParameters();
    }
  }
  render(): JSX.Element {
    const { subMenuRoute } = this.props;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const submenuLinks = Object.keys(this.subPages).map((subPageName: any) =>
      this.buildLink(subPageName)
    );
    const actionMenu = this.getActionMenu(subMenuRoute);
    const dataView = this.getDataView(subMenuRoute);
    const modal = this.buildModal();

    return (
      <>
        <ContentLayout submenu={submenuLinks} actionMenu={actionMenu} dataView={dataView} />
        {modal}
      </>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setColumnFilters = (subMenuRoute: State.DriveSubmenu, newFilters: Filter[]): any => {
    const newFilterSet = { ...this.state.tableColumnFilters, [subMenuRoute]: newFilters };
    this.setState(prevState => ({
      ...prevState,
      tableColumnFilters: newFilterSet
    }));
  };

  private getDataView = (subMenuRoute: State.DriveSubmenu): JSX.Element => {
    if (subMenuRoute === "Parameter Categories") {
      const passProps = {
        ...this.props,
        toggleModal: this.toggleModal,
        setColumnFilters: this.setColumnFilters,
        groupName: "Drive"
      };
      return React.createElement(this.subPages[subMenuRoute].dataView, passProps);
    } else if (subMenuRoute in this.subPages) {
      const passProps = {
        ...this.props,
        toggleModal: this.toggleModal,
        setColumnFilters: this.setColumnFilters
      };
      return React.createElement(this.subPages[subMenuRoute].dataView, passProps);
    }
    return <>Error</>;
  };

  private getActionMenu = (subMenuRoute: State.DriveSubmenu): JSX.Element | null => {
    if (subMenuRoute === "Drive Bindings") {
      // Because bindings does not have an action menu
      return null;
    }

    return (
      <ActionMenu
        {...this.props}
        itemName={this.subPages[subMenuRoute].itemName}
        groupName={this.groupName}
        selectedColumnsName={subMenuRoute}
        dataFieldName={this.subPages[subMenuRoute].PdApiField}
        setExtraProperties={this.subPages[subMenuRoute].setExtraProperties}
        createEmpty={this.subPages[subMenuRoute].createEmpty}
        columnFilters={this.state.tableColumnFilters[subMenuRoute]}
      />
    );
  };

  private buildLink = (subPageName: State.DriveSubmenu): JSX.Element => {
    // To dynamically build urls we use the itenName and make it plural, so -s || -y => -ies
    let parsedLink = this.subPages[subPageName].itemName.replace(" ", "-");
    parsedLink = parsedLink.slice(-1) === "y" ? parsedLink.slice(0, -1) + "ies" : parsedLink + "s";
    return (
      <NavigationLink
        key={subPageName}
        handleOnClick={() => this.handleLinkClick(subPageName)}
        name={subPageName}
        to={"/" + this.groupName.toLowerCase() + "/" + parsedLink + "/"}
      />
    );
  };

  private handleLinkClick = (subPageName: Api.AnyItem) => {
    this.props.editActions.cancelEditing;
    if (subPageName === "Drive Faults" && this.props.pdApi.driveFaults === undefined) {
      this.props.pdApiActions.fetchDriveFaults();
    }
    if (subPageName === "Drive Parameters" && this.props.pdApi.driveParameters === undefined) {
      this.props.pdApiActions.fetchDriveParameters();
    }
  };

  private buildModal = () => {
    const domainName = this.props.domainName ? " Domain: " + this.props.domainName : "";
    const content = (
      <span>
        Are you sure you want to delete this {this.subPages[this.props.subMenuRoute].itemName}?
        <br />
        {this.props.objectIdentifier}
        <br /> {domainName}
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={this.state.isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        /*note={`${this.props.subMenuRoute === "Drive Signal Groups"
            ? "NOTE: this will also remove all Drive Signals that belong to this group!"
            : ""
          }`}
          */
        note={""}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={() =>
          this.props.editActions.delete(
            this.subPages[this.props.subMenuRoute].PdApiField as Api.OptionsGrud
          )
        }
        onCancel={this.toggleModal}
      />
    );
  };

  /*
   * Toggles the modal based on previous state.
   */
  private toggleModal = (): void => {
    this.setState(prevState => ({
      ...prevState,
      isModalOpen: !prevState.isModalOpen
    }));
  };
}

const mapStateToProps = (state: State.Root, ownProps: OwnProps) => ({
  columns: state.columns,
  pdApi: state.pdApi,
  mainState: state.main,
  isLoadingFaults: state.loadingStatus.isDriveFaultsLoading,
  isLoadingParameters: state.loadingStatus.isDriveParametersLoading,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  objectIdentifier: editedObjectIdentifierSelector(state.editView, ownProps.subMenuRoute),
  domainName: domainSelector(state.editView),
  filterOptions: state.filterOptions
});

const mapDispatchToProps = (dispatch: Dispatch<PdApiActions | ColumnActions | EditActions>) => ({
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
  columnActions: bindActionCreators({ ...columnActions }, dispatch),
  editActions: bindActionCreators({ ...editActions }, dispatch),
  mainActions: bindActionCreators({ ...mainActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Drive);
